import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { ReactComponent as MoreArrow } from "../assets/svg/ReadMore.svg";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { apiRoutes } from "../api/apiRoutes";
import useLocale from "../locales";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setLoading } from "../redux/loading/loadingSlice";
import { createSlugWithId, createSectionId } from "../utils/slugUtils";
import { Helmet } from "react-helmet";
import getImageUrl from "../utils/imageUrl";

// Import Lexical dependencies
import { createHeadlessEditor } from '@lexical/headless';
import { $generateHtmlFromNodes } from '@lexical/html';

// Import necessary Lexical nodes
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { CodeNode } from '@lexical/code';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { AutoLinkNode } from '@lexical/link';

const BlogDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const { slug } = useParams();
  const location = useLocation();
  const [blog, setBlog] = useState(null);
  const [relatedBlog, setRelatedBlog] = useState([]);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const titleRef = useRef(null);
  const [searchParams] = useSearchParams();
  const { textData } = useLocale();
  const { blogsheader, tableheader, relatedblogs, moreoption } = textData;
  const lang = sessionStorage.getItem("lang");

  // Get title from navigation state
  const passedTitle = location.state?.title;

  // Configure Lexical editor config with all node types
  const editorConfig = {
    namespace: 'BlogEditor',
    nodes: [
      HeadingNode,
      QuoteNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      CodeNode,
      HorizontalRuleNode,
    ],
    onError: (error) => {
      console.error('Lexical Editor Error:', error);
    },
  };

  // Custom function to manually parse Lexical JSON to HTML
  const lexicalToHtml = (contentJson) => {
    if (!contentJson) return '';
    
    try {
      const data = typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson;
      
      if (!data || !data.root) return '';

      const processNode = (node) => {
        if (!node) return '';

        // Handle different node types
        switch (node.type) {
          case 'root':
            return node.children?.map(child => processNode(child)).join('') || '';

          case 'paragraph':
            return `<p class="mb-4 leading-relaxed">${node.children?.map(child => processNode(child)).join('') || ''}</p>`;

          case 'heading':
            const level = node.tag || 'h2';
            const headingText = node.children?.map(child => child.text || '').join('') || '';
            const headingId = createSectionId(headingText);
            
            // Add appropriate classes based on heading level
            let headingClass = 'font-bold mb-3';
            if (level === 'h1') headingClass += ' text-3xl mt-6';
            if (level === 'h2') headingClass += ' text-2xl mt-5';
            if (level === 'h3') headingClass += ' text-xl mt-4';
            if (level === 'h4') headingClass += ' text-lg mt-3';
            
            return `<${level} id="${headingId}" class="${headingClass}">${node.children?.map(child => processNode(child)).join('') || ''}</${level}>`;

          case 'text':
            let text = node.text || '';
            // Handle text formatting
            if (node.format & 1) text = `<strong>${text}</strong>`; // Bold
            if (node.format & 2) text = `<em>${text}</em>`; // Italic
            if (node.format & 4) text = `<u>${text}</u>`; // Underline
            if (node.format & 8) text = `<code class="bg-gray-100 px-1 rounded font-sans">${text}</code>`; // Code
            if (node.format & 16) text = `<del>${text}</del>`; // Strikethrough
            return text;

          case 'image':
            // Handle image nodes
            const imageSrc = node.src || node.url || '';
            const imageAlt = node.altText || 'Blog image';
            const imageWidth = node.width || 'auto';
            const imageHeight = node.height || 'auto';
            
            return `
              <figure class="my-6">
                <img 
                  src="${imageSrc}" 
                  alt="${imageAlt}"
                  width="${imageWidth}"
                  height="${imageHeight}"
                  class="max-w-full h-auto rounded-lg mx-auto"
                  loading="lazy"
                />
                ${node.caption ? `<figcaption class="text-center text-sm text-gray-500 mt-2">${node.caption}</figcaption>` : ''}
              </figure>
            `;

          case 'image-inline':
            // Handle inline images
            return `
              <img 
                src="${node.src || node.url || ''}" 
                alt="${node.altText || 'Blog image'}"
                class="inline-block max-w-full h-auto rounded"
                loading="lazy"
              />
            `;

          case 'table':
            return `
              <div class="overflow-x-auto my-6">
                <table class="w-full border-collapse">
                  ${node.children?.map(child => processNode(child)).join('') || ''}
                </table>
              </div>
            `;

          case 'tablecell':
            const cellType = node.headerState === 3 ? 'th' : 'td';
            const cellClass = 'border border-gray-300 p-3 text-left';
            return `<${cellType} class="${cellClass}">${node.children?.map(child => processNode(child)).join('') || ''}</${cellType}>`;

          case 'tablerow':
            return `<tr class="even:bg-gray-50">${node.children?.map(child => processNode(child)).join('') || ''}</tr>`;

          case 'list':
            const listType = node.tag === 'ul' ? 'ul' : 'ol';
            const listClass = `my-4 pl-5 ${listType === 'ul' ? 'list-disc' : 'list-decimal'}`;
            return `<${listType} class="${listClass}">${node.children?.map(child => processNode(child)).join('') || ''}</${listType}>`;

          case 'listitem':
            return `<li class="">${node.children?.map(child => processNode(child)).join('') || ''}</li>`;

          case 'quote':
            return `
              <blockquote class="border-l-4 border-primary pl-4 py-2 my-4 italic text-gray-700">
                ${node.children?.map(child => processNode(child)).join('') || ''}
              </blockquote>
            `;

          case 'code':
            return `
              <pre class="bg-gray-100 p-4 rounded-lg my-4 overflow-x-auto">
                <code class="text-sm">${node.children?.map(child => processNode(child)).join('') || ''}</code>
              </pre>
            `;

          case 'code-highlight':
            return `
              <pre class="bg-gray-100 p-4 rounded-lg my-4 overflow-x-auto">
                <code class="text-sm">${node.children?.map(child => processNode(child)).join('') || ''}</code>
              </pre>
            `;

          case 'link':
            return `
              <a 
                href="${node.url || '#'}" 
                class="text-primary hover:underline font-sans"
                target="_blank"
                rel="noopener noreferrer"

              >
                ${node.children?.map(child => processNode(child)).join('') || ''}
              </a>
            `;

          case 'autolink':
            return `
              <a 
                href="${node.url || '#'}" 
                class="text-primary hover:underline font-sans"
                target="_blank"
                rel="noopener noreferrer"
              >
                ${node.children?.map(child => processNode(child)).join('') || ''}
              </a>
            `;

          case 'horizontalrule':
            return '<hr class="my-8 border-t border-gray-300" />';

          case 'linebreak':
            return '<br />';

          default:
            // If we don't recognize the node type, just process its children
            if (node.children && Array.isArray(node.children)) {
              return node.children.map(child => processNode(child)).join('');
            }
            
            // If it has a text property, return it
            if (node.text) {
              return node.text;
            }
            
            return '';
        }
      };

      return processNode(data.root);
    } catch (error) {
      console.error('Error parsing Lexical content:', error);
      return '<p class="text-red-500">Error rendering content</p>';
    }
  };

  useEffect(() => {
    async function fetchBlog() {
      try {
        dispatch(setLoading(true));
        const response = await axiosInstance.get(
          `/api/blog/${slug}`,
          {
            params: { lang: i18n.language },
          }
        );
        const { success, message, data } = response?.data || {};
        if (success) {
          setBlog(data || null);
          setError(null);

          // If URL contains a numeric id (or any non-slug path), replace it
          // with the slug stored in the blog object so the address bar never
          // shows ids. replace: true keeps the id URL out of browser history.
          if (data?.slug && data.slug !== slug) {
            navigate(`/blog/${data.slug}`, { replace: true });
          }
          
          // Convert contentJson to HTML when blog data is received
          if (data?.contentJson) {
            const html = lexicalToHtml(data.contentJson);
            setHtmlContent(html);
          }
        } else {
          setBlog(null);
          setError(message || "Something went wrong");
        }
      } catch (err) {
        const message =
          err?.response?.data?.message || err.message || "Something went wrong";
        setError(message);
        setBlog(null);
      } finally {
        dispatch(setLoading(false));
      }
    }
    
    async function fetchRelatedBlog() {
      try {
        dispatch(setLoading(true));
        const response = await axiosInstance.get(apiRoutes.GET_ALL_BLOG, {
          params: { lang: i18n.language },
        });
        setRelatedBlog(response?.data?.data);
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        dispatch(setLoading(false));
      }
    }
    
    fetchBlog();
    fetchRelatedBlog();
  }, [slug, lang, i18n.language]);

  const extractH2Headings = (contentJson) => {
    if (!contentJson) return [];

    try {
      const parsedContent = typeof contentJson === 'string' 
        ? JSON.parse(contentJson) 
        : contentJson;
      
      if (!parsedContent?.root) return [];

      const headings = [];

      const traverse = (node) => {
        // Check if current node is an h2
        if (node.type === 'heading' && node.tag === "h2" && node.children) {
          // Extract text from children
          const text = node.children
            .filter((child) => child.text)
            .map((child) => child.text)
            .join("");

          if (text) {
            headings.push(text);
          }
        }

        // Recursively traverse children
        if (node.children && Array.isArray(node.children)) {
          node.children.forEach((child) => traverse(child));
        }
      };

      traverse(parsedContent.root);
      return headings;
    } catch (error) {
      console.error('Error extracting H2 headings:', error);
      return [];
    }
  };

  const scrollToTabs = () => {
    titleRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const scrollToSection = (headingText) => {
    const sectionId = createSectionId(headingText);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <>
      <Header />
      {error ? (
        <div className="max-w-[1280px] mx-auto px-3 sm:px-5 lg:px-10 py-10 text-center">
          <p className="text-lg sm:text-xl font-semibold text-red-600">
            {error}
          </p>
        </div>
      ) : blog ? (
        <div>
          <div className="max-w-[1280px] md:px-0 px-0 mx-3 sm:mx-5 lg:mx-10 xl:mx-auto ">
            <div className="mt-6 ">
              <div className="flex item-center pb-8 lg:pb-10 xl:pb-[61px]">
                <span
                  className="text-blue-shade text-sm sm:text-base md:text-lg xl:text-xl font-medium cursor-pointer"
                  onClick={() => navigate("/")}
                >
                  {blogsheader.home}
                </span>
                <span className="mx-2 text-blue-shade">{">"}</span>
                <span
                  className="text-blue-shade text-sm sm:text-base md:text-lg xl:text-xl font-medium cursor-pointer"
                  onClick={() => navigate("/blog")}
                >
                  {blogsheader.blogs}
                </span>
                <span className="mx-2 text-gray-font">{">"}</span>
                <span className="text-gray-font text-sm sm:text-base md:text-lg xl:text-xl font-medium">
                  {blog?.title}
                </span>
              </div>
            </div>

            <div>
              <img
                src={getImageUrl(blog?.blogImage)}
                alt={blog?.title}
                className="w-full rounded-[30px] md:h-[607px] h-[267.16px] object-cover"
              />

              <div>
                <div className="flex flex-col md:flex-row gap-3 sm:gap-4 lg:gap-6 xl:gap-[35px] py-10 font-sans">
                  {/* Left Content */}
                  <div className="flex-1">
                    {/* Title */}
                    <h1
                      className="text-lg sm:text-xl md:text-2xl lg:text-[26px] xl:text-4xl font-bold text-black pb-3"
                      ref={titleRef}
                    >
                      {blog?.title}
                    </h1>

                    {/* Author & Date */}
                    <p className="text-sm sm:text-base md:text-lg text-gray-font font-medium mb-[24px]">
                      {blog?.authorName} | {blog?.publishedDate?.split("T")[0]}
                    </p>
                    <p className="mb-[16px] text-gray-700 leading-relaxed">{blog?.description}</p>
                    
                    {/* Render the converted HTML content */}
                    <div
                      className="blog-content"
                      dangerouslySetInnerHTML={{ 
                        __html: htmlContent || blog?.contentHtml || '' 
                      }}
                    />
                    
                    {/* Add debug info in development */}
                    {process.env.NODE_ENV === 'development' && !htmlContent && !blog?.contentHtml && (
                      <div className="mt-4 p-4 bg-yellow-100 border border-yellow-400 rounded">
                        <p className="font-bold">Debug: No content to display</p>
                        <p>contentJson available: {blog?.contentJson ? 'Yes' : 'No'}</p>
                        <details className="mt-2">
                          <summary className="cursor-pointer text-primary">View Raw JSON</summary>
                          <pre className="mt-2 text-xs overflow-auto max-h-96 bg-gray-800 text-white p-2 rounded">
                            {JSON.stringify(blog?.contentJson, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </div>

                  {/* Right Side Table of Contents */}
                  <aside className="md:max-w-[300px] lg:max-w-[350px] xl:max-w-[406px] w-full h-fit bg-primary-bg p-5 rounded-[20px] sticky top-4">
                    <h3 className="text-gray-font text-sm font-normal mb-[22px]">
                      {tableheader.contenthead}
                    </h3>
                    <h2 className="text-base sm:text-lg lg:text-xl font-bold text-black mb-4">
                      {blog?.title}
                    </h2>
                    <ul className="list-decimal list-inside text-base sm:text-base lg:text-lg text-black flex flex-col gap-2 sm:gap-3 lg:gap-5">
                      {blog?.contentJson &&
                        extractH2Headings(blog.contentJson).map(
                          (heading, index) => (
                            <li 
                              key={index}
                              onClick={() => scrollToSection(heading)}
                              className="hover:text-primary transition-colors duration-200 cursor-pointer"
                            >
                              {heading}
                            </li>
                          )
                        )}
                    </ul>
                  </aside>
                </div>
              </div>
            </div>

            <div className="mt-5 mb-[67px]">
              <h2 className="text-center text-lg sm:text-xl lg:text-[22px] xl:text-[26px] font-bold text-black pb-4 sm:pb-6 md:pb-10">
                {relatedblogs.related}{" "}
                <span className="text-primary">{blogsheader.blogs}</span>
              </h2>
              <div className="grid sm:grid-cols-2 md882:grid-cols-3 gap-x-7 gap-y-[20px] sm:gap-x-4 sm:gap-y-[30px] lg:gap-x-5 lg:gap-y-[30px] xl:gap-x-7 xl:gap-y-[50px] mt-6">
                {Array.isArray(relatedBlog) &&
                  relatedBlog
                    ?.filter((item) => item.id !== blog?.id)
                    .slice(0, 3)
                    .map((item) => {
                      return (
                        <div
                          key={item.id}
                          className="rounded-[30px] overflow-hidden object-contain border border-solid border-gray-light bg-white hover:shadow-lg transition-shadow duration-300"
                        >
                          <img
                            className="w-full object-cover xl:h-[304px] md:h-[275px] h-[195px]"
                            src={getImageUrl(item?.blogImage)}
                            alt={item?.title}
                          />

                          <div className="p-5 md882:p-3 lg:p-5">
                            <h3 className="flex justify-between text-gray-font text-sm font-medium pb-4 sm:pb-[23px]">
                              <span>{item?.authorName}</span>
                              <span>{item?.publishedDate?.split("T")[0]}</span>
                            </h3>
                            <div className="text-base md:text-lg xl:text-xl font-bold text-black line-clamp-1 h-7 my-4">
                              {item?.title}
                            </div>

                            <p className="text-gray-font text-[15px] sm:text-base font-medium mb-4 sm:pb-[23px] h-[70px] line-clamp-3">
                              {item?.description}
                            </p>

                            <Link
                              to={`/blog/${item.slug}`}
                              // state={{ title: item.title, blogId: item.id }}
                              className="text-primary font-medium text-base md882:text-lg lg:text-xl flex items-center gap-2 hover:gap-3 transition-all duration-300"
                            >
                              {moreoption.readmore}
                              <MoreArrow />
                            </Link>
                          </div>
                        </div>
                      );
                    })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default BlogDetail;